import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import {
  CheckCircle,
  Package,
  ShoppingBag,
  Mail,
  ArrowRight,
  Loader2,
  XCircle,
  Clock,
  Home
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const paymentMethod = searchParams.get("payment_method");
  const [status, setStatus] = useState("loading"); // loading, success, pending, error
  const [orderDetails, setOrderDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // If invoice payment, show success immediately
    if (paymentMethod === "invoice" && orderId) {
      setStatus("invoice_success");
      setOrderDetails({ order_id: orderId });
      localStorage.removeItem("allstillas_cart");
      return;
    }
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else if (!orderId) {
      setStatus("error");
    }
  }, [sessionId, orderId, paymentMethod]);

  const pollPaymentStatus = async (sid, attempt = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus("pending");
      return;
    }

    try {
      const res = await axios.get(`${API}/shop/checkout/status/${sid}`);
      const data = res.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setOrderDetails(data);
        // Clear cart on successful payment
        localStorage.removeItem("allstillas_cart");
        return;
      } else if (data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setAttempts(attempt + 1);
      setTimeout(() => pollPaymentStatus(sid, attempt + 1), pollInterval);
    } catch (err) {
      console.error("Error checking payment status:", err);
      if (attempt >= maxAttempts - 1) {
        setStatus("error");
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempt + 1), pollInterval);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <Card className="bg-[#0A0A0A] border-white/10 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-[#23F7DD]/20 rounded-full flex items-center justify-center animate-pulse">
                <Loader2 className="w-10 h-10 text-[#23F7DD] animate-spin" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('webshop.checkoutSuccess.verifying')}
              </h2>
              {attempts > 0 && (
                <p className="text-xs text-[#64748B] mt-4">
                  {t('webshop.checkoutSuccess.verifyingAttempt', { current: attempts, max: 10 })}
                </p>
              )}
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('webshop.checkoutSuccess.paymentProcessed')}
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {t('webshop.checkoutSuccess.thankYouOrder')}
              </p>
              
              {orderDetails && (
                <div className="p-4 bg-white/5 rounded-lg mb-6 text-left">
                  <div className="flex items-center gap-2 text-[#94A3B8] mb-2">
                    <Package className="w-4 h-4" />
                    <span>{t('webshop.checkoutSuccess.orderNumber')}</span>
                  </div>
                  <p className="text-white font-mono text-lg">{orderDetails.order_number || sessionId}</p>
                  {orderDetails.amount_total && (
                    <p className="text-green-400 font-bold mt-2">
                      {t('webshop.checkoutSuccess.total')} {(orderDetails.amount_total / 100).toFixed(2)} {orderDetails.currency?.toUpperCase()}
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-center gap-2 text-[#94A3B8] mb-6">
                <Mail className="w-4 h-4" />
                <span className="text-sm">{t('webshop.checkoutSuccess.confirmationSent')}</span>
              </div>

              <div className="space-y-3">
                <Link to="/webshop" className="block">
                  <Button className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    {t('webshop.checkoutSuccess.continueShopping')}
                  </Button>
                </Link>
                <Link to="/" className="block">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5">
                    <Home className="w-4 h-4 mr-2" />
                    {t('webshop.checkoutSuccess.backToHome')}
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === "pending" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <Clock className="w-10 h-10 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('webshop.checkoutSuccess.paymentPending')}
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {t('webshop.checkoutSuccess.paymentPendingDesc')}
              </p>
              <Link to="/">
                <Button className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white hover:from-yellow-600 hover:to-orange-700">
                  <Home className="w-4 h-4 mr-2" />
                  {t('webshop.checkoutSuccess.backToHome')}
                </Button>
              </Link>
            </>
          )}

          {status === "invoice_success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-[#23F7DD]/20 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle className="w-10 h-10 text-[#23F7DD]" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('webshop.checkoutSuccess.orderComplete')}
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {t('webshop.checkoutSuccess.invoiceDesc')}
              </p>
              
              {orderDetails && (
                <div className="p-4 bg-white/5 rounded-lg mb-6 text-left">
                  <div className="flex items-center gap-2 text-[#94A3B8] mb-2">
                    <Package className="w-4 h-4" />
                    <span>{t('webshop.checkoutSuccess.orderId')}</span>
                  </div>
                  <p className="text-white font-mono text-lg">{orderDetails.order_id}</p>
                </div>
              )}

              <div className="p-4 bg-[#23F7DD]/10 rounded-lg border border-[#23F7DD]/30 mb-6">
                <div className="flex items-center justify-center gap-2 text-[#23F7DD]">
                  <Mail className="w-5 h-5" />
                  <span className="text-sm font-medium">{t('webshop.checkoutSuccess.invoiceWillBeSent')}</span>
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">{t('webshop.checkoutSuccess.paymentDue')}</p>
              </div>

              <div className="space-y-3">
                <Link to="/webshop" className="block">
                  <Button className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    {t('webshop.checkoutSuccess.continueShopping')}
                  </Button>
                </Link>
                <Link to="/" className="block">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5">
                    <Home className="w-4 h-4 mr-2" />
                    {t('webshop.checkoutSuccess.backToHome')}
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-2">
                {t('webshop.checkoutSuccess.somethingWrong')}
              </h2>
              <p className="text-[#94A3B8] mb-6">
                {t('webshop.checkoutSuccess.paymentNotVerified')}
              </p>
              <div className="space-y-3">
                <Link to="/cart" className="block">
                  <Button className="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white hover:from-red-600 hover:to-pink-700">
                    {t('webshop.checkoutSuccess.tryAgain')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/" className="block">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5">
                    <Home className="w-4 h-4 mr-2" />
                    {t('webshop.checkoutSuccess.backToHome')}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
              <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
            </div>
            <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
            <div className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
              <span className="text-[#23F7DD] text-[10px]">⚡</span>
              <span className="text-[10px] text-[#94A3B8]">Powered by</span>
              <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
            </div>
          </Link>
          <p className="text-sm text-[#64748B]">© 2026 Doserne AS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
